import {ref, shallowRef, triggerRef, reactive, computed, watch, onMounted} from 'vue';
import {useStore} from 'vuex';
import _ from 'lodash-es';
import {
    expressionToString,
    getBusinessObject,
    getDefaultExpression
} from '@/lib/util/ModelUtil';
import {getOperatorOptions, getTableOptions} from '@/src/utils/attributeUtils';
import {OP, usePending} from "@/src/queries/pending";

export function useExpressionEditor(props, emit) {
    const store = useStore();

    const editMode = computed(() => store.getters['graphStore/getExpressionCurrentEditId'] === props.element.id);



// Reactive State
    const labelRef = ref()
    const item = shallowRef(props.element);
    const validForms = reactive([]);
    const columns = ref([]);
    const filteredColumns = ref([]);
    const compareColumns = ref([]);
    const compareFilteredColumns = ref([]);
    const filteredTables = ref([]);
    const filteredCompareTables = ref([]);
    const filteredActivities = ref([])

    const suggestedActivities = computed(() => store.getters["repoStore/getActivities"])

    const lastLockTouchAt = ref(0)
    const touchEditModeSession = () => {
        if (!store.getters['graphStore/getEditMode']) {
            return
        }
        const now = Date.now()
        if (now - lastLockTouchAt.value < 3000) {
            return
        }
        lastLockTouchAt.value = now
        store.dispatch('graphStore/setUnlockInterval')
    }

    watch(() => props.element, (newElement) => {
        item.value = newElement;
    });

    watch(() => props.element, (newElement) => {
        item.value = newElement;
    });

    const compareTypeOptions = computed(() => {
        let options = [
            {text: 'Static', value: 'static'},
            {text: 'Compare Attributes', value: 'dynamic'}
        ]
        if (props.elementType === 'od:Activity') {
            options.push({text: 'Compare Between Events', value: 'dynamic_events'})
        }
        return options
    })

    const toggleEditMode = () => {
        touchEditModeSession()
        store.commit('graphStore/setExpressionCurrentEditId', props.element.id);
        focusLabel()
    };

    const focusLabel = () => {
        setTimeout(() => {
            if (!labelRef.value) {
                return
            }
            const inputElement = labelRef.value[0].$el.querySelector('input');
            if (inputElement && document.activeElement !== inputElement) {
                inputElement.focus();
            }
        },300)
    }


// Computed: Vuex getters
    const isLoading = usePending([OP.GET_TABLE_COLUMNS, OP.GET_TABLES]);
    const modeler = computed(() => store.getters['graphStore/getModeler']);

// Helpers
    function getProp(prop) {
        return item.value[prop];
    }

    async function updateProp(prop, value) {
        if (prop === 'pql' && value === '') {
            emit('delete');
            return;
        }
        Object.assign(item.value, {[prop]: value});
        touchEditModeSession()
        triggerRef(item);

        emit('update');
    }

    function validate(i, val) {
        validForms[i] = val;
    }

    function getTableColumns(table) {
        return store.dispatch('repoStore/getTableColumns', table);
    }


// Lifecycle
    onMounted(async () => {
        if (tableOptions.value.length) {
            columns.value = await getTableColumns(getProp('event_attribute.table'));
            compareColumns.value = await getTableColumns(getProp('compare_value.event_attribute.table'));
        }
        focusLabel()
    });

// Computed Properties
    const defaultProps = computed(() => getDefaultExpression(item.value));
    const propFields = computed(() => Object.keys(defaultProps.value));
    const propValues = computed(() => _.pick(getBusinessObject(item.value), propFields.value));

    const columnOrTableLoading = computed(() =>
        isLoading.value
    );

    const tableOptions = computed(() => getTableOptions());
    const operatorOptions = computed(() =>
        getOperatorOptions(getProp('compare_value.type'), getProp('compare_value.is_numeric'))
    );


    const fields = computed(() => {
        const isNullCompare = getProp('operator').includes('NULL');
        const isIn = getProp('operator').includes('IN');
        return [
            {
                elementName: 'InputText', dataField: 'label', label: 'Label',
                class: 'editorField',
                ref: labelRef,
                infoTooltipLeft: 'Add a user-friendly label describing the check', condition: true
            },
            {
                elementName: 'AutoComplete', dataField: 'event_attribute.table', label: 'Table Name',
                inputchars: '\\w\\s/', class: 'tableField editorField', required: true,
                disabled: columnOrTableLoading.value || props.itemType == 'filter',
                placeholder: columnOrTableLoading.value ? 'Loading...' : 'Select a table',
                suggestions: filteredTables.value, dropdown: true,
                onComplete: event => filteredTables.value = tableOptions.value.filter(t => t.toLowerCase().includes(event.query.toLowerCase())),
                onChange: async () => {
                    const tbl = getProp('event_attribute.table');
                    columns.value = tableOptions.value.includes(tbl) ? await getTableColumns(tbl) : [];
                }, condition: true,
            },
            {
                elementName: 'AutoComplete', dataField: 'event_attribute.attribute', label: 'Column Name',
                suggestions: filteredColumns.value, dropdown: true,
                disabled: columnOrTableLoading.value, placeholder: columnOrTableLoading.value ? 'Loading...' : 'Select a column',
                inputchars: '\\w\\s/', class: 'attributeField  editorField', required: true,
                onComplete: event => filteredColumns.value = columns.value.filter(c => c.toLowerCase().includes(event.query.toLowerCase())),
                condition: true
            },
            {
                elementName: 'Select',
                dataField: 'operator',
                label: 'Operator',
                options: operatorOptions.value,
                class: 'operatorField editorField',
                condition: true
            },
            {
                elementName: 'Select',
                dataField: 'compare_value.type',
                label: 'Compare Value Type',
                options: compareTypeOptions.value,
                optionLabel: 'text',
                optionValue: 'value',
                class: 'compareTypeField editorField',
                condition: !isNullCompare && props.elementType != 'filter'
            },
            {
                elementName: isIn ? 'InputTags' : 'InputText',
                dataField: 'compare_value.value',
                type: 'text',
                label: 'Compare value',
                showwhitespace: true,
                required: getProp('compare_value.is_numeric') || isIn,
                class: 'compareValueField editorField',
                condition: getProp('compare_value.type') === 'static' && !isNullCompare
            },
            {
                elementName: 'ToggleSwitch',
                dataField: 'compare_value.is_numeric',
                label: 'Is Numeric',
                class: 'isNumericField',
                default: false,
                disabled: getProp('operator').includes('LIKE'),
                condition: getProp('compare_value.type') === 'static' && !isNullCompare
            },
            {
                elementName: 'AutoComplete',
                suggestions: filteredActivities.value,
                dropdown: true,
                onComplete: (event) => {
                    filteredActivities.value = suggestedActivities.value.filter(act => act.toLowerCase().includes(event.query.toLowerCase()))
                },
                dataField: 'compare_value.reference_event',
                class: 'eventField',
                label: 'Compare Activity',
                required: true,
                condition: props.elementType === 'od:Activity' && getProp('compare_value.type') === 'dynamic_events'
            },
            {
                elementName: 'AutoComplete',
                class: 'editorField',
                dataField: 'compare_value.event_attribute.table',
                label: 'Compare Table Name',
                inputchars: '\\w\\s/',
                suggestions: filteredCompareTables.value,
                dropdown: true,
                disabled: columnOrTableLoading.value,
                placeholder: columnOrTableLoading.value ? 'Loading...' : 'Select a table',
                required: true,
                invalidValue: sameAttribute.value,
                invalidValueMessage: 'Compare table/attribute must be different for same reference event',
                onComplete: event => filteredCompareTables.value = tableOptions.value.filter(t => t.toLowerCase().includes(event.query.toLowerCase())),
                onChange: async () => {
                    const ct = getProp('compare_value.event_attribute.table');
                    compareColumns.value = tableOptions.value.includes(ct) ? await getTableColumns(ct) : [];
                },
                condition: getProp('compare_value.type') === 'dynamic' || getProp('compare_value.type') === 'dynamic_events'
            },
            {
                elementName: 'AutoComplete',
                dataField: 'compare_value.event_attribute.attribute',
                label: 'Compare Column Name',
                suggestions: compareFilteredColumns.value,
                dropdown: true,
                disabled: columnOrTableLoading.value,
                placeholder: columnOrTableLoading.value ? 'Loading...' : 'Select a column',
                required: true,
                inputchars: '\\w\\s/',
                class: 'attributeField editorField',
                onComplete: event => compareFilteredColumns.value = compareColumns.value.filter(c => c.toLowerCase().includes(event.query.toLowerCase())),
                condition: getProp('compare_value.type') === 'dynamic' || getProp('compare_value.type') === 'dynamic_events'
            },
            // add boolean compare_reference_default
            {
                elementName: 'ToggleSwitch',
                dataField: 'compare_reference_default',
                class: 'compareReferenceDefaultField',
                default: true,
                label: 'No Violation if Compare Activity does not occur',
                infoTooltipLeft: 'If set to true, no violation will be raised if the compare activity has not occurred.',
                condition: props.elementType === 'od:Activity' && getProp('compare_value.type') === 'dynamic_events'
            }
        ].filter(f => f.condition);
    });

    const sameAttribute = computed(() => {
        const type = getProp('compare_value.type');
        if (type === 'static' || type === 'dynamic_events') {
            return false;
        }
        const t1 = getProp('event_attribute.table'), a1 = getProp('event_attribute.attribute');
        const t2 = getProp('compare_value.event_attribute.table'),
            a2 = getProp('compare_value.event_attribute.attribute');
        if (!t1 || !a1 || !t2 || !a2) return false;
        return getProp('reference_event') === getProp('compare_value.reference_event') && t1.toLowerCase() === t2.toLowerCase() && a1.toLowerCase() === a2.toLowerCase();
    });

    const isInvalid = computed(() => {

        return editMode.value ? _.some(validForms, v => !v) : !getProp('enabled')
    });

// Two-way enabled binding
    const enabled = computed({
        get: () => getProp('enabled'),
        set: val => updateProp('enabled', val)
    });

    const stringValue = computed(() => expressionToString(item.value));

// Watchers
    watch(isInvalid, val => enabled.value = !val);
    watch(tableOptions, async (newVal, oldVal) => {
        if (newVal.length !== oldVal.length) {
            columns.value = await getTableColumns(getProp('event_attribute.table'));
            compareColumns.value = await getTableColumns(getProp('compare_value.event_attribute.table'));
        }
    });

// Methods
    function onClose() {
        if (getProp('mode') === 'pql' && getProp('pql') === '') {
            emit('delete');
        }
        store.commit('graphStore/setExpressionCurrentEditId', null);
    }

    function updateField(field, value) {
        updateProp(field, value);
        if (field === 'compare_value.type' && value === 'dynamic' && /IN|LIKE|NULL/.test(getProp('operator'))) {
            updateProp('operator', '=');
        }
    }

    return {
        item,
        validForms,
        columns,
        compareColumns,
        filteredTables,
        filteredColumns,
        filteredActivities,
        compareFilteredColumns,
        editMode,
        toggleEditMode,
        getProp,
        updateProp,
        validate,
        onClose,
        updateField,
        stringValue,
        isInvalid,
        fields,
        propValues,
    };
}
